import React, {
  useState,
  useMemo,
  useEffect,
} from "react"

import { getRegionCurrency, isVisibleIn } from "@utils/region"
import { findInitialVariant } from "@utils/product"
import { getPrice } from "@utils/products/pricing"
import ScreenReaderText from "../screen-reader-text"
import { ReactComponent as Close } from "@svgs/close.svg";
import { onEnterKeyPress } from "@src/utils/general"
import { trackEvent } from "@src/services/analytics"
import { useQuickViewStore, useProductStore } from "@src/hooks/useStore"

const InlineCrossSell = ({
  products,
  trackUpsellClicks,
}) => {
  return (
    <div className="w-full">
      {products
        .filter((product) => isVisibleIn(product.visibilityRegions))
        .map(product => {
          const {
            setQuickViewOpen,
            setQuickViewProduct,
            setQuickViewVariant,
            setQuickViewType,
          } = useQuickViewStore(["setQuickViewOpen", "setQuickViewProduct", "setQuickViewVariant", "setQuickViewType"]);

          const {addedCrossSellProducts, removeAddedCrossSellProducts} = useProductStore(["addedCrossSellProducts", "removeAddedCrossSellProducts"]);

          const { title, variants, priceFrom, priceTo } = product;

          const openProductQuickView = () => {
            trackUpsellClicks();
            setQuickViewProduct(product);
            setQuickViewVariant(initialVariant)
            setQuickViewOpen(true);
            setQuickViewType('cross-sell');
            trackUpsellClick({ product });
          };

          const isCrossSellAdded = useMemo(() => {
            return addedCrossSellProducts?.some(addedProduct => variants?.some(variant => addedProduct.product.shopifyIdBase64Encoded === variant.shopifyIdBase64Encoded));
          }, [addedCrossSellProducts])
          const [active, setActive] = useState(isCrossSellAdded)

          const initialVariant = variants && (findInitialVariant(variants, true) || variants[0])

          const price = getPrice(initialVariant);

          const removeProduct = () => {
            removeAddedCrossSellProducts([product])
            trackUpsellClicks();
          }

          const updateProduct = () => {
            removeProduct();
            openProductQuickView();
          }

          useEffect(() => {
            setActive(isCrossSellAdded);
          }, [isCrossSellAdded])

          return (
            <div key={`cross-sell-product-${product?.yotpoProductId || product?._id}`} className="first:border-t-1 border-b-1 border-solid border-darker-gray">
              <div className={`cursor-pointer flex w-full py-2.5 group`}>
                {active ? (
                  <span className="flex relative justify-between w-full border-l-4 border-solid border-safety-green pl-2.5">
                    <ScreenReaderText srText={`Added ${title} only for ${price}`} className={'w-full'} onClick={updateProduct} onKeyDown={(e) => onEnterKeyPress(e, updateProduct)} tabIndex={0} ariaHidden={false}>
                      <span className="font-medium text-safety-green">Added</span> {title} | <span className="text-safety-green">{price}{+priceFrom < +priceTo ? '+' : ''}</span>
                    </ScreenReaderText>
                    <Close className={'w-4'} onClick={removeProduct} />
                  </span>
                ) : (
                  <span className="flex relative justify-between w-full group-hover:pl-2.5 duration-300" >
                    <ScreenReaderText srText={`Add ${title} only for ${price}`} className={'w-full'} onClick={openProductQuickView} onKeyDown={(e) => onEnterKeyPress(e, openProductQuickView)} tabIndex={0} ariaHidden={false}>
                      <span className="font-medium">Add</span> {title} | <span className="text-safety-green">{price}{+priceFrom < +priceTo ? '+' : ''}</span>
                    </ScreenReaderText>
                  </span>
                )}
              </div>
            </div>
          )
        })}
    </div>
  )
}

export default InlineCrossSell

const trackUpsellClick = ({ product }) => {
  const productDetails = {
    name: product?.title,
  }

  setTimeout(() => {
    try {
      trackEvent("upsellCheckboxClicked", {
        ecommerce: {
          currencyCode: getRegionCurrency(),
          detail: {
            products: [productDetails],
          },
        },
      })
    } catch (e) {
      console.log(e)
    }
  }, 150)
}
